import React, { useState } from "react";

import MoreIcon from "@uui/DotsHorizontal";
import RefreshIcon from "@uui/RefreshCcw04";
import SendIcon from "@uui/Send01";
import TrashIcon from "@uui/Trash02";

import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import useZapier from "@app/analytics/hooks/integrations/useZapier";
import {
  FormSubLabel,
  IntegrationMenu,
} from "@app/analytics/Integrations/components/styles";
import type { ZapierIntegration } from "@app/analytics/types/zapierTypes";
import { toast } from "@app/common/hooks/useToasts";
import { isResponseError } from "@app/common/utils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Button from "@rui/Button";
import ConfirmationModal from "@rui/ConfirmationModal";
import CopyButton from "@rui/CopyButton";
import IconButton from "@rui/IconButton";
import MenuItem from "@rui/MenuItem";
import { Column, Row } from "@rui/Stack";
import TextField from "@rui/TextField";

const ApiKeyInput = styled(TextField)({
  width: "450px",
  maxWidth: "calc(100% - 100px)",
  backgroundColor: "#FFF",
  height: "100%",
  "& .MuiInputBase-root": {
    height: "100%",
  },
});

const ApiKey = ({ apiKey }: { apiKey: ZapierIntegration }) => {
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);

  const {
    anchorElement,
    onClose: onMenuClose,
    open,
    onClick,
  } = useAnchorElement();

  const { regenerateApiKey, sendTestRequest, deleteApiKey } = useZapier();

  const handleClickRegenerate = () => {
    onMenuClose();
    setConfirmingRegenerate(true);
  };

  const handleCloseModal = () => setConfirmingRegenerate(false);

  const handleConfirmRegenerate = () => {
    regenerateApiKey.mutate(
      { id: apiKey.id },
      {
        onError: () => {
          handleCloseModal();
          toast.error(
            "Something went wrong and the API key could not be regenerated. Please try again or contact support@read.ai if the problem persists."
          );
        },
        onSuccess: () => handleCloseModal(),
      }
    );
  };

  const handleSendTestRequest = () => {
    onMenuClose();
    sendTestRequest.mutate(undefined, {
      onError: (error) => {
        const status = isResponseError(error) ? error.response.status : null;
        toast.error(
          `Your test API call resulted in an error${
            status ? ` (${status})` : ""
          }`
        );
      },
      onSuccess: () => {
        toast.success("Your test API call was successful!");
      },
    });
  };

  const handleDeleteApiKey = () => {
    onMenuClose();
    deleteApiKey.mutate(
      { id: apiKey.id },
      {
        onSuccess: () => {
          toast.success("API key successfully deleted!");
        },
        onError: () => {
          toast.error(
            "Something went wrong and the API key could not be deleted. Please try again later or reach out to support@read.ai if the problem persists."
          );
        },
      }
    );
  };

  return (
    <>
      <Column>
        <FormSubLabel>API Key</FormSubLabel>
        <Row variant="left" spacing={1.5}>
          <ApiKeyInput value={apiKey.key} InputProps={{ readOnly: true }} />
          <CopyButton
            copyValue={apiKey.key}
            renderButton={({ onClick }, copied) => (
              <Button
                variant="outlined"
                onClick={onClick}
                sx={{ bgcolor: "#FFF" }}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            )}
          />
          <IconButton onClick={onClick} size="large">
            <MoreIcon />
          </IconButton>
        </Row>
      </Column>
      <IntegrationMenu
        anchorEl={anchorElement}
        open={open}
        onClose={onMenuClose}
      >
        <MenuItem icon={<SendIcon />} onClick={handleSendTestRequest}>
          Send test call
        </MenuItem>
        <MenuItem icon={<RefreshIcon />} onClick={handleClickRegenerate}>
          Regenerate key
        </MenuItem>
        <MenuItem icon={<TrashIcon />} onClick={handleDeleteApiKey}>
          Delete key
        </MenuItem>
      </IntegrationMenu>
      {confirmingRegenerate && (
        <ConfirmationModal
          title="Regenerate key?"
          open={confirmingRegenerate}
          onClose={handleCloseModal}
          onConfirm={handleConfirmRegenerate}
          loading={regenerateApiKey.isPending}
        >
          <Typography variant="bodyPrimaryReg" color="shades.neutral.700">
            The current API key will be deleted, and you will need to copy the
            new key into Zapier
          </Typography>
        </ConfirmationModal>
      )}
    </>
  );
};

export default ApiKey;
